import { memo } from 'react';
import { FileText, DollarSign, Target, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { formatUSD } from '../../utils/formatting';

interface MetricsGridProps {
  loading: boolean;
  myQuotes: number;
  thisMonthQuotes: number;
  myQuotesValue: number;
  thisMonthValue: number;
  thisWeekQuotes: number;
}

export const MetricsGrid = memo(function MetricsGrid({
  loading,
  myQuotes,
  thisMonthQuotes,
  myQuotesValue,
  thisMonthValue,
  thisWeekQuotes
}: MetricsGridProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        title="My Quotes"
        value={loading ? '-' : String(myQuotes)}
        subtext={`+${loading ? '-' : thisMonthQuotes} this month`}
        icon={FileText}
        loading={loading}
      />

      <MetricCard
        title="Total Quote Value"
        value={loading ? '-' : formatUSD(myQuotesValue)}
        subtext={`${loading ? '-' : formatUSD(thisMonthValue)} this month`}
        icon={DollarSign}
        loading={loading}
      />

      <MetricCard
        title="Average Quote Value"
        value={loading ? '-' : myQuotes > 0 ? formatUSD(myQuotesValue / myQuotes) : '$0.00'}
        subtext="per quote value"
        icon={Target}
        loading={loading}
      />

      <MetricCard
        title="Quotes This Week"
        value={loading ? '-' : String(thisWeekQuotes)}
        subtext="past 7 days"
        icon={Calendar}
        loading={loading}
      />
    </div>
  );
});

interface MetricCardProps {
  title: string;
  value: string;
  subtext: string;
  icon: React.ComponentType<{ className?: string }>;
  loading: boolean;
}

const MetricCard = memo(function MetricCard({
  title,
  value,
  subtext,
  icon: Icon,
  loading
}: MetricCardProps) {
  return (
    <Card className="border-border bg-card hover:shadow-lg hover:shadow-accent/5 transition-all duration-300 hover:border-accent/20">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-card-foreground">{title}</CardTitle>
        <Icon className="h-4 w-4 text-accent" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-foreground">{value}</div>
        <p className="text-xs text-muted-foreground">{subtext}</p>
      </CardContent>
    </Card>
  );
});
